import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { Location, NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { TranslateModule } from '@ngx-translate/core';
import { Lightbox, LightboxModule } from 'ng-gallery/lightbox';
import { Gallery, GalleryImageComponent, GalleryItemDef, GalleryRef } from 'ng-gallery';

import { Globals } from '@core/globals';
import { IconComponent } from '@atoms/icon/icon.component';
import { ImageComponent } from '@atoms/image/image.component';
import { LoaderComponent } from '@atoms/loader/loader.component';
import { PreloadImageDirective } from '@directives/preload-image.directive';
import { RippleDirective } from '@directives/ripple.directive';
import { SrcsetPipe } from '@pipes/srcset.pipe';
import { GlobalsService } from '@services/globals.service';
import { GtmService } from '@services/gtm.service';
import { SsrService } from '@services/ssr.service';
import { GalleryItem } from '@models/gallery.model';

@Component({
  selector: 'app-car-detail-gallery',
  imports: [GalleryImageComponent, GalleryItemDef, NgClass, TranslateModule, LightboxModule, IconComponent, ImageComponent, LoaderComponent, PreloadImageDirective, RippleDirective, SrcsetPipe],
  templateUrl: './car-detail-gallery.component.html',
  styleUrls: ['./car-detail-gallery.component.scss'],
})
export class CarDetailGalleryComponent implements OnInit, OnChanges {
  private cd = inject(ChangeDetectorRef);
  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);
  private location = inject(Location);
  private gallery = inject(Gallery);
  private lightbox = inject(Lightbox);
  public globals = inject(Globals);
  private globalsService = inject(GlobalsService);
  private gtmService = inject(GtmService);
  private ssrService = inject(SsrService);
  @Input() id?: number;
  @Input() items: GalleryItem[] = [];
  @Input('three-sixty') threeSixty?: string | null;
  @Input() title?: string;
  @Input() pdf?: string;
  @Output() toggleSavedCars: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('gallerySwiper') gallerySwiper!: ElementRef;
  @ViewChild('thumbnailsInner') thumbnailsInner!: ElementRef;
  @ViewChild('thumbnailsInnerMobile') thumbnailsInnerMobile!: ElementRef;
  @ViewChild(GalleryItemDef, { static: true }) itemDef!: GalleryItemDef;
  public threeSixtyUrl?: SafeResourceUrl;
  public threeSixtyActive: boolean = true;
  public galleryId = 'gallery-lightbox';
  public active: number = 0;
  public scrollStart: boolean = true;
  public scrollEnd: boolean = true;
  public loaded: boolean = true;
  private swipeTimeout: any;

  @HostListener('window:resize') onResize(): void {
    this.scrollCheck();
  }

  @HostListener('document:mousedown', ['$event.target']) click(event: HTMLElement): void {
    if (event.closest('.g-nav-prev') || event.closest('.g-nav-next')) this.gtmService.pushGtmPageType({ event: 'click_galerie_big_listovani' });
    else if (event.closest('gallery-thumb')) this.gtmService.pushGtmPageType({ event: 'click_galerie_big_zalozka_fotky' });
  }

  back(): void {
    if (this.ssrService.isServer()) return;
    let previousPage = this.globalsService.previousUrl();
    if (previousPage) {
      if (previousPage.includes(this.globals.routes()['list'].url)) {
        const char = previousPage.includes('#') ? '&' : '#';
        const fragmentUrl = previousPage.split('#');
        if (fragmentUrl.length > 1) {
          const fragmentParams = this.globalsService.getFragmentParams(fragmentUrl[1]);
          if (fragmentParams['scrollToId']) {
            previousPage = previousPage.replace(fragmentParams['scrollToId'], (this.id ?? '').toString());
          } else {
            previousPage = previousPage + char + 'scrollToId=' + this.id;
          }
        } else {
          previousPage = previousPage + char + 'scrollToId=' + this.id;
        }
        this.router.navigateByUrl(previousPage);
      } else {
        this.location.back();
      }
    } else {
      this.router.navigate([this.globals.routes()['list'].url]);
    }
  }

  share(): void {
    const title = this.title ? this.title + ' Mototechna' : 'Mototechna';
    navigator.share({
      title: title,
      text: title,
      url: window.location.href,
    });
  }

  threeSixtyOpen(): void {
    this.change(0);
    this.threeSixtyActive = true;
  }

  openLightbox(index: number): void {
    this.lightbox.open(index, this.galleryId, {
      panelClass: 'fullscreen',
    });
  }

  change(active: number, e?: Event, disableScroll?: boolean): void {
    this.threeSixtyActive = false;
    if (this.active === active || active < 0 || active >= this.items.length) return;
    this.active = active;
    if (window.innerWidth < 1000) {
      const el: HTMLElement = this.thumbnailsInnerMobile?.nativeElement;
      const item = el.querySelector('.car-detail__gallery-bottom .car-detail__gallery-thumbnails-item:not(.car-detail__gallery-thumbnails-item--active)');
      const itemWidth = (item ? item.clientWidth : 200) + 4;
      const scroll = active * itemWidth;
      el.scroll({
        behavior: 'smooth',
        left: scroll,
      });
    } else {
      const el: HTMLElement = this.thumbnailsInner?.nativeElement;
      const elHeight = el.clientHeight;
      const item = el.querySelector('.car-detail__gallery-main .car-detail__gallery-thumbnails-item:not(.car-detail__gallery-thumbnails-item--active)');
      const itemHeight = (item ? item.clientHeight : 200) + 4;
      const scroll = (active / 2) * itemHeight - elHeight / 2 + itemHeight / 2;
      el.scroll({
        behavior: 'smooth',
        top: scroll,
      });
    }

    if (!disableScroll) this.scrollToPhoto();

    e?.preventDefault();
    return;
  }

  swipe(): void {
    if (this.ssrService.isServer()) return;
    const swiper = this.gallerySwiper?.nativeElement;
    const items = swiper.querySelectorAll('.car-detail__gallery-wrap');

    if (this.swipeTimeout) clearTimeout(this.swipeTimeout);
    this.swipeTimeout = setTimeout(() => {
      const scrollLeft = swiper.scrollLeft;
      const itemWidth = items[0].offsetWidth;
      const index = Math.round(scrollLeft / itemWidth);
      this.change(index, undefined, true);
      this.cd.detectChanges();
    }, 250);
  }

  scrollToPhoto(): void {
    if (this.ssrService.isServer()) return;
    const swiper = this.gallerySwiper?.nativeElement;
    const items = swiper.querySelectorAll('.car-detail__gallery-wrap');
    if (!items || items.length === 0) return;

    const targetItem = items[this.active];
    const scrollY = window.scrollY;
    targetItem.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'start' });
    window.scrollTo(window.scrollX, scrollY);
    this.cd.detectChanges();
  }

  prev(): void {
    if (this.threeSixtyActive) {
      this.threeSixtyActive = false;
      return;
    }
    const active: number = this.active > 0 ? this.active - 1 : this.items.length - 1;
    this.change(active);
  }

  next(): void {
    if (this.threeSixtyActive) {
      this.threeSixtyActive = false;
      return;
    }
    const active: number = this.active < this.items.length - 1 ? this.active + 1 : 0;
    this.change(active);
  }

  scrollCheck(): void {
    if (this.ssrService.isServer()) return;
    if (window.innerWidth < 1000) {
      const el: HTMLElement = this.thumbnailsInnerMobile?.nativeElement;
      const scroll: number = el.scrollLeft;
      this.scrollStart = scroll <= 16;
      this.scrollEnd = scroll >= el.scrollWidth - el.clientWidth - 16;
    } else {
      const el: HTMLElement = this.thumbnailsInner?.nativeElement;
      const scroll: number = el.scrollTop;
      this.scrollStart = scroll <= 6;
      this.scrollEnd = scroll >= el.scrollHeight - el.clientHeight - 6;
    }
    this.cd.detectChanges();
  }

  scroll(direction: 'up' | 'down' | 'left' | 'right'): void {
    const el: HTMLElement = direction === 'up' || direction === 'down' ? this.thumbnailsInner?.nativeElement : this.thumbnailsInnerMobile?.nativeElement;
    const item = el.querySelector('.car-detail__gallery-bottom .car-detail__gallery-thumbnails-item:not(.car-detail__gallery-thumbnails-item--active)');
    const itemHeight = item ? item.clientHeight : 200;
    const itemWidth = item ? item.clientWidth : 200;
    let scroll: number;

    if (direction === 'up') {
      scroll = el.scrollTop - el.clientHeight + itemHeight;
      scroll = scroll < 0 ? 0 : scroll;
    } else if (direction === 'down') {
      scroll = el.scrollTop + el.clientHeight - itemHeight;
      scroll = scroll > el.scrollHeight - el.clientHeight ? el.scrollHeight - el.clientHeight : scroll;
    } else if (direction === 'left') {
      scroll = el.scrollLeft - el.clientWidth + itemWidth;
      scroll = scroll < 0 ? 0 : scroll;
    } else {
      scroll = el.scrollLeft + el.clientWidth - itemWidth;
      scroll = scroll > el.scrollWidth - el.clientWidth ? el.scrollWidth - el.clientWidth : scroll;
    }

    if (direction === 'up' || direction === 'down') {
      el.scroll({
        behavior: 'smooth',
        top: scroll,
      });
    } else {
      el.scroll({
        behavior: 'smooth',
        left: scroll,
      });
    }

    this.scrollCheck();
    this.gtm('listovani');
  }

  imageLoaded(): void {
    this.loaded = true;
  }

  gtm(id?: string): void {
    if (!id) return;
    this.gtmService.pushGtmPageType({ event: 'click_galerie_' + id });
  }

  ngOnInit(): void {
    const galleryRef = this.gallery.ref(this.galleryId, {
      itemTemplate: this.itemDef.templateRef,
    });
    galleryRef.setConfig({
      thumbs: true,
      counter: true,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] && changes['items'].currentValue !== changes['items'].previousValue) {
      this.change(0);
      this.threeSixtyActive = this.threeSixty ? true : false;
      const galleryRef: GalleryRef = this.gallery.ref(this.galleryId, {
        itemTemplate: this.itemDef.templateRef,
      });

      for (let item of this.items) {
        galleryRef.add(item, false);
      }
    }
    if (changes['threeSixty'] && changes['threeSixty'].currentValue !== changes['threeSixty'].previousValue) {
      this.threeSixtyActive = this.threeSixty ? true : false;
      this.threeSixtyUrl = undefined;
      if (this.threeSixty) {
        const threeSixtySplit: string[] = this.threeSixty ? this.threeSixty.split('/') : [];
        if (threeSixtySplit.length > 1) {
          const threeSixtyUrl: string = 'https://photo360.aaaauto.cz/new-360-2025/?id=' + threeSixtySplit[threeSixtySplit.length - 2] + '&lang=' + this.globals.language() + '&custom-controls=true&responsive=true&web-type=mototechna';
          this.threeSixtyUrl = this.sanitizer.bypassSecurityTrustResourceUrl(threeSixtyUrl);
        }
      }
    }
    if (this.ssrService.isServer()) return;
    setTimeout(() => this.scrollCheck());
  }

  ngOnDestroy(): void {
    if (this.ssrService.isServer()) return;
    if (this.swipeTimeout) clearTimeout(this.swipeTimeout);
    const galleryRef: GalleryRef = this.gallery.ref(this.galleryId);
    galleryRef.destroy();
  }
}
